import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface LedgerEntryForm {
  id: string;
  entry_date: string;
  amount_tnd: string;
  debit_account: string;
  credit_account: string;
  label: string;
  piece_reference: string;
}

interface RapprocheurResult {
  statement_summary: { source: 'ofx' | 'csv' | 'preparsed'; transactions_count: number; bank_account_sce_code: string };
  bank_transactions: Array<{
    id: string;
    date: string;
    amount_millimes: number;
    description: string;
    reference?: string;
  }>;
  matches: Array<{
    bank_transaction_id: string;
    ledger_entry_ids: string[];
    confidence: number;
    method: 'exact' | 'strong' | 'weak' | 'multi';
    reasoning: string;
    date_delta_days: number;
  }>;
  unmatched_bank_transaction_ids: string[];
  unmatched_ledger_entry_ids: string[];
  stats: {
    bank_transactions_count: number;
    ledger_entries_count: number;
    matched_count: number;
    match_rate: number;
    total_unmatched_bank_amount_millimes: number;
    total_unmatched_ledger_amount_millimes: number;
    breakdown_by_method: { exact: number; strong: number; weak: number; multi: number };
  };
}

const SAMPLE_OFX = `OFXHEADER:100
DATA:OFXSGML
VERSION:102
<OFX>
<BANKMSGSRSV1><STMTTRNRS><STMTRS>
<CURDEF>TND
<BANKACCTFROM><BANKID>BIAT<ACCTID>10001234<ACCTTYPE>CHECKING</BANKACCTFROM>
<BANKTRANLIST><DTSTART>20260401<DTEND>20260430
<STMTTRN><TRNTYPE>DEBIT<DTPOSTED>20260403<TRNAMT>-150.500<FITID>tx-1<NAME>STEG FACTURE MARS</STMTTRN>
<STMTTRN><TRNTYPE>CREDIT<DTPOSTED>20260415<TRNAMT>2500.000<FITID>tx-2<NAME>VIREMENT CLIENT ACME</STMTTRN>
</BANKTRANLIST>
</STMTRS></STMTTRNRS></BANKMSGSRSV1>
</OFX>`;

const SAMPLE_CSV_BIAT = `Date opération;Date valeur;Libellé;Débit;Crédit;Solde
03/04/2026;03/04/2026;FACTURE STEG MARS 2026;150,500;;1000.000
15/04/2026;15/04/2026;VIREMENT CLIENT ACME;;2500,000;3500.000`;

const SAMPLE_LEDGER: LedgerEntryForm[] = [
  {
    id: 'e1',
    entry_date: '2026-04-03',
    amount_tnd: '150.500',
    debit_account: '614',
    credit_account: '532',
    label: 'STEG facture mars 2026',
    piece_reference: 'F-2026-STEG',
  },
  {
    id: 'e2',
    entry_date: '2026-04-15',
    amount_tnd: '2500.000',
    debit_account: '532',
    credit_account: '411',
    label: 'Virement Client ACME',
    piece_reference: '',
  },
];

const METHOD_STYLE: Record<RapprocheurResult['matches'][number]['method'], string> = {
  exact: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  strong: 'bg-blue-100 text-blue-900 border-blue-300',
  weak: 'bg-amber-100 text-amber-900 border-amber-300',
  multi: 'bg-purple-100 text-purple-900 border-purple-300',
};

const METHOD_LABEL: Record<RapprocheurResult['matches'][number]['method'], string> = {
  exact: 'Exact',
  strong: 'Fort',
  weak: 'Faible',
  multi: 'Multi',
};

type StatementMode = 'ofx' | 'csv' | 'preparsed';

export function RapprocheurPage() {
  const [mode, setMode] = useState<StatementMode>('ofx');
  const [content, setContent] = useState<string>(SAMPLE_OFX);
  const [csvFormat, setCsvFormat] = useState<'biat' | 'attijari' | 'bh' | 'generic'>('biat');
  const [bankAccount, setBankAccount] = useState('532');
  const [ledger, setLedger] = useState<LedgerEntryForm[]>(SAMPLE_LEDGER);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function loadSample(m: StatementMode) {
    setMode(m);
    if (m === 'ofx') setContent(SAMPLE_OFX);
    else if (m === 'csv') {
      setContent(SAMPLE_CSV_BIAT);
      setCsvFormat('biat');
    } else setContent('');
  }

  function updateEntry(idx: number, patch: Partial<LedgerEntryForm>) {
    setLedger((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  }
  function addEntry() {
    setLedger((prev) => [
      ...prev,
      {
        id: `e${prev.length + 1}`,
        entry_date: '2026-04-15',
        amount_tnd: '0.000',
        debit_account: '614',
        credit_account: '532',
        label: '',
        piece_reference: '',
      },
    ]);
  }
  function removeEntry(idx: number) {
    setLedger((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const ledger_entries = ledger.map((entry) => ({
        id: entry.id,
        entry_date: entry.entry_date,
        amount_millimes: tndStringToMillimes(entry.amount_tnd),
        debit_account: entry.debit_account,
        credit_account: entry.credit_account,
        label: entry.label,
        ...(entry.piece_reference.trim() ? { piece_reference: entry.piece_reference.trim() } : {}),
      }));
      let statement: Record<string, unknown>;
      if (mode === 'ofx') {
        statement = { format: 'ofx', content };
      } else if (mode === 'csv') {
        statement = { format: 'csv', content, csv_format: csvFormat };
      } else {
        statement = { format: 'preparsed', transactions: [] };
      }
      const res = await api.invokeAgent('rapprocheur', {
        payload: {
          bank_account_sce_code: bankAccount,
          statement,
          ledger_entries,
        },
        fiscal_reference_date: new Date().toISOString().slice(0, 10),
      });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Rapprocheur"
      module="Comptabilité · Phase 2"
      model="Sonnet 4.6"
      description={
        <>
          Rapprochement bancaire automatisé — parsing OFX (1.x SGML / 2.x XML) ou CSV (BIAT,
          Attijari, BH), matching à 4 passes (exact / fort / faible / multi-écritures), détection
          des écarts. Tout le matching est déterministe ; Sonnet ne produit que la prose.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Compte bancaire à rapprocher">
          <Field label="Code SCE" hint="Compte bancaire (ex. 532, sous-comptes 5321, 5322…)">
            <input className={inputCls + ' font-mono'} value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} required />
          </Field>
        </FormCard>

        <FormCard
          title="Relevé bancaire"
          action={
            <div className="flex gap-2 text-xs">
              {(['ofx', 'csv', 'preparsed'] as StatementMode[]).map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => loadSample(m)}
                  className={`px-2 py-1 rounded border transition ${
                    mode === m
                      ? 'bg-ink text-cream border-ink'
                      : 'bg-white text-ink border-border hover:border-gold'
                  }`}
                >
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          }
        >
          {mode === 'csv' && (
            <Field label="Format CSV">
              <select className={inputCls} value={csvFormat} onChange={(e) => setCsvFormat(e.target.value as typeof csvFormat)}>
                <option value="biat">BIAT (point-virgule, FR dates, débit/crédit)</option>
                <option value="attijari">Attijari (virgule, ISO dates, signed)</option>
                <option value="bh">BH (point-virgule, FR dates, débit/crédit)</option>
                <option value="generic">Générique (column_map à fournir)</option>
              </select>
            </Field>
          )}
          {mode === 'preparsed' ? (
            <div className="text-xs text-taupe italic p-3 border border-border rounded bg-cream/30">
              Mode preparsed : envoyez les transactions déjà normalisées via l'API directement (utile pour les imports batch). Pas d'UI dans cette page bootstrap.
            </div>
          ) : (
            <Field label="Contenu du relevé" hint={mode === 'ofx' ? 'Format OFX 1.x (SGML) ou 2.x (XML)' : 'Coller le CSV exporté depuis votre espace bancaire'}>
              <textarea
                rows={10}
                className={inputCls + ' font-mono text-xs'}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
              />
            </Field>
          )}
        </FormCard>

        <FormCard
          title={`Écritures comptables (${ledger.length})`}
          action={
            <button type="button" onClick={addEntry} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">
              + Ajouter écriture
            </button>
          }
        >
          <div className="space-y-3">
            {ledger.map((entry, idx) => (
              <div key={idx} className="border border-border rounded p-3 bg-cream/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-taupe">Écriture #{idx + 1}</span>
                  {ledger.length > 1 && (
                    <button type="button" onClick={() => removeEntry(idx)} className="text-xs text-red-700 hover:text-red-900">Supprimer</button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <Field label="ID"><input className={inputCls + ' font-mono'} value={entry.id} onChange={(e) => updateEntry(idx, { id: e.target.value })} required /></Field>
                  <Field label="Date écriture"><input type="date" className={inputCls} value={entry.entry_date} onChange={(e) => updateEntry(idx, { entry_date: e.target.value })} required /></Field>
                  <Field label="Montant (TND)"><input className={inputCls + ' font-mono'} value={entry.amount_tnd} onChange={(e) => updateEntry(idx, { amount_tnd: e.target.value })} /></Field>
                  <Field label="Compte débit"><input className={inputCls + ' font-mono'} value={entry.debit_account} onChange={(e) => updateEntry(idx, { debit_account: e.target.value })} required /></Field>
                  <Field label="Compte crédit"><input className={inputCls + ' font-mono'} value={entry.credit_account} onChange={(e) => updateEntry(idx, { credit_account: e.target.value })} required /></Field>
                  <Field label="Libellé"><input className={inputCls} value={entry.label} onChange={(e) => updateEntry(idx, { label: e.target.value })} required /></Field>
                  <Field label="Référence pièce" hint="Optionnel (boost match exact)"><input className={inputCls + ' font-mono'} value={entry.piece_reference} onChange={(e) => updateEntry(idx, { piece_reference: e.target.value })} /></Field>
                </div>
              </div>
            ))}
          </div>
        </FormCard>

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Rapprochement…' : 'Rapprocher'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <RapprocheurResultView result={result as RapprocheurResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function RapprocheurResultView({ result }: { result: RapprocheurResult }) {
  const txMap = new Map(result.bank_transactions.map((t) => [t.id, t]));
  return (
    <div className="space-y-5">
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Synthèse</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <Stat label="Tx bancaires" value={String(result.stats.bank_transactions_count)} />
          <Stat label="Écritures" value={String(result.stats.ledger_entries_count)} />
          <Stat
            label="Taux de match"
            value={`${(result.stats.match_rate * 100).toFixed(0)}%`}
            highlight
          />
          <Stat
            label="Non rapprochés (banque)"
            value={formatMillimes(Math.abs(result.stats.total_unmatched_bank_amount_millimes))}
          />
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Répartition par méthode</div>
        <div className="grid grid-cols-4 gap-2 text-sm">
          {(['exact', 'strong', 'weak', 'multi'] as const).map((m) => (
            <div key={m} className={`p-2 rounded border text-center ${METHOD_STYLE[m]}`}>
              <div className="text-xs uppercase tracking-wider">{METHOD_LABEL[m]}</div>
              <div className="font-mono text-xl font-bold">{result.stats.breakdown_by_method[m]}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">
          Matches proposés ({result.matches.length})
        </div>
        {result.matches.length === 0 ? (
          <div className="text-sm text-taupe italic p-3 border border-border rounded bg-cream/30">
            Aucun match. Vérifier les pools de transactions et écritures.
          </div>
        ) : (
          <div className="space-y-2">
            {result.matches.map((m, i) => {
              const tx = txMap.get(m.bank_transaction_id);
              return (
                <div key={i} className="border border-border rounded p-3 bg-white">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded border uppercase tracking-wider ${METHOD_STYLE[m.method]}`}>
                      {METHOD_LABEL[m.method]}
                    </span>
                    <span className="text-xs text-taupe">confiance {(m.confidence * 100).toFixed(0)}%</span>
                    <span className="text-xs text-taupe">Δ {m.date_delta_days >= 0 ? '+' : ''}{m.date_delta_days} j</span>
                    {tx && (
                      <span className={`ml-auto font-mono text-sm ${tx.amount_millimes < 0 ? 'text-red-800' : 'text-emerald-800'}`}>
                        {formatMillimes(Math.abs(tx.amount_millimes))} {tx.amount_millimes < 0 ? 'D' : 'C'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <div className="text-taupe">Banque</div>
                      <div className="font-mono">{m.bank_transaction_id}</div>
                      {tx && (
                        <>
                          <div>{tx.description}</div>
                          <div className="text-taupe">{tx.date}</div>
                        </>
                      )}
                    </div>
                    <div>
                      <div className="text-taupe">Écriture(s)</div>
                      <div className="font-mono">{m.ledger_entry_ids.join(', ')}</div>
                    </div>
                  </div>
                  <div className="text-xs text-taupe mt-2 italic">{m.reasoning}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {result.unmatched_bank_transaction_ids.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">
            Transactions bancaires non rapprochées ({result.unmatched_bank_transaction_ids.length})
          </div>
          <ul className="text-sm space-y-1">
            {result.unmatched_bank_transaction_ids.map((id) => {
              const tx = txMap.get(id);
              return (
                <li key={id} className="p-2 border border-amber-300 bg-amber-50/40 rounded text-xs flex items-center justify-between">
                  <span>
                    <span className="font-mono">{id}</span>
                    {tx && <> · {tx.date} · {tx.description}</>}
                  </span>
                  {tx && (
                    <span className={`font-mono ${tx.amount_millimes < 0 ? 'text-red-800' : 'text-emerald-800'}`}>
                      {formatMillimes(tx.amount_millimes)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {result.unmatched_ledger_entry_ids.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">
            Écritures non rapprochées ({result.unmatched_ledger_entry_ids.length})
          </div>
          <ul className="text-xs font-mono space-y-1">
            {result.unmatched_ledger_entry_ids.map((id) => (
              <li key={id} className="p-2 border border-rose-300 bg-rose-50/40 rounded">{id}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-3 rounded border text-center ${highlight ? 'border-gold bg-gold/10' : 'border-border bg-white'}`}>
      <div className="text-xs uppercase tracking-wider text-taupe">{label}</div>
      <div className={`font-mono text-lg ${highlight ? 'font-bold' : ''}`}>{value}</div>
    </div>
  );
}
